import React, { useCallback, useEffect, useState } from 'react'
import Cropper from 'react-easy-crop'
import './PortraitCropModal.css'

const FULL_SIZE = 512
const SM_SIZE = 128

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.src = src
  })
}

async function cropToBlob(imageSrc, pixelCrop, size) {
  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('No se pudo crear el canvas')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size
  )
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('No se pudo generar la imagen recortada'))
          return
        }
        resolve(blob)
      },
      'image/png',
      0.92
    )
  })
}

/**
 * Modal de recorte 1:1 para retratos de emisor.
 * @param {{ imageSrc: string, onCancel: () => void, onConfirm: (blobs: { full: Blob, sm: Blob }) => Promise<void>|void }} props
 */
export default function PortraitCropModal({ imageSrc, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleConfirm = async () => {
    if (!croppedAreaPixels || busy) return
    setBusy(true)
    setError('')
    try {
      const [full, sm] = await Promise.all([
        cropToBlob(imageSrc, croppedAreaPixels, FULL_SIZE),
        cropToBlob(imageSrc, croppedAreaPixels, SM_SIZE),
      ])
      await onConfirm?.({ full, sm })
    } catch (e) {
      setError(e?.message || 'No se pudo recortar la imagen')
      setBusy(false)
    }
  }

  return (
    <div className="portrait-crop-modal" role="dialog" aria-modal="true" aria-labelledby="portrait-crop-title">
      <div className="portrait-crop-modal__backdrop" onClick={() => !busy && onCancel?.()} aria-hidden="true" />
      <div className="portrait-crop-modal__panel">
        <header className="portrait-crop-modal__header">
          <h3 id="portrait-crop-title">Recortar retrato</h3>
          <button
            type="button"
            className="portrait-crop-modal__close"
            onClick={() => !busy && onCancel?.()}
            aria-label="Cerrar"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <div className="portrait-crop-modal__stage">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid
          />
        </div>

        <div className="portrait-crop-modal__controls">
          <label className="portrait-crop-modal__zoom">
            <span>Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={busy}
            />
          </label>
          <p className="portrait-crop-modal__hint">
            Arrastra la imagen para elegir la zona visible del retrato (cuadrado).
          </p>
          {error ? (
            <p className="portrait-crop-modal__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="portrait-crop-modal__footer">
          <button
            type="button"
            className="portrait-crop-modal__btn portrait-crop-modal__btn--ghost"
            onClick={() => onCancel?.()}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="portrait-crop-modal__btn"
            onClick={handleConfirm}
            disabled={busy || !croppedAreaPixels}
          >
            {busy ? 'Guardando…' : 'Usar recorte'}
          </button>
        </footer>
      </div>
    </div>
  )
}
